import { Editor, Path, Point, Range, Text, Transforms } from 'slate'

import { TranscriptDelta } from 'src/models/TranscriptDelta'

const SYNC_EDITOR_TO_TRANSCRIPT_MAP = new WeakMap<Editor, boolean>()

type EditableSpan = { start: number; end: number; path: Path }

const editableSpans = (editor: Editor): EditableSpan[] => {
    const spans: EditableSpan[] = []
    let offset = 0
    for (const [node, path] of Editor.nodes(editor, { at: [0], match: Text.isText })) {
        if (node.isEditable) {
            spans.push({ start: offset, end: offset + node.text.length, path })
        }
        offset += node.text.length
    }

    return spans
}

const pointIsEditable = (editor: Editor, point: Point): boolean => {
    const [node] = Editor.node(editor, point.path)

    return Text.isText(node) && node.isEditable === true
}

type EditableEdge = { distance: number; edge: number; point: Point }
type CharacterGap = { start: number; end: number }

/** Half-open ranges of character offsets that no editable span covers. The last range runs to infinity. */
const nonEditableGaps = (spans: readonly EditableSpan[]): CharacterGap[] => {
    const gaps: CharacterGap[] = []
    let cursor = 0
    for (const span of spans) {
        if (span.start > cursor) {
            gaps.push({ start: cursor, end: span.start })
        }
        cursor = Math.max(cursor, span.end)
    }
    gaps.push({ start: cursor, end: Number.POSITIVE_INFINITY })

    return gaps
}

const nearerEdge = (current: EditableEdge | null, edge: number, edgePoint: Point, offset: number): EditableEdge => {
    const distance = Math.abs(edge - offset)
    if (current === null || distance < current.distance || (distance === current.distance && edge < current.edge)) {
        return { distance, edge, point: edgePoint }
    }

    return current
}

const clampPointToEditable = (editor: Editor, point: Point, spans: readonly EditableSpan[]): Point => {
    if (pointIsEditable(editor, point)) {
        return point
    }

    const offset = TranscriptEditor.getParagraphOffsetFromPoint(editor, point)
    let best: EditableEdge | null = null
    for (const span of spans) {
        if (span.end <= offset) {
            best = nearerEdge(best, span.end, Editor.end(editor, span.path), offset)
        }
        if (span.start >= offset) {
            best = nearerEdge(best, span.start, Editor.start(editor, span.path), offset)
        }
    }

    return best === null ? point : best.point
}

/** Pull `focus` to the anchor's side of the first non-editable run between the two points. */
const focusOffHole = (editor: Editor, anchor: Point, focus: Point, spans: readonly EditableSpan[]): Point => {
    const anchorOffset = TranscriptEditor.getParagraphOffsetFromPoint(editor, anchor)
    const focusOffset = TranscriptEditor.getParagraphOffsetFromPoint(editor, focus)
    if (anchorOffset === focusOffset) {
        return focus
    }

    const from = Math.min(anchorOffset, focusOffset)
    const to = Math.max(anchorOffset, focusOffset)
    const crossed = nonEditableGaps(spans).filter((gap) => gap.start < to && gap.end > from)
    const gap = anchorOffset < focusOffset ? crossed[0] : crossed[crossed.length - 1]
    if (gap === undefined) {
        return focus
    }

    if (anchorOffset < focusOffset) {
        const span = spans.find((item) => item.end === Math.max(gap.start, anchorOffset))

        return span === undefined ? focus : Editor.end(editor, span.path)
    }

    const span = spans.find((item) => item.start === Math.min(gap.end, anchorOffset))

    return span === undefined ? focus : Editor.start(editor, span.path)
}

export const TranscriptEditor = {
    isSyncEditorToTranscriptEnabled: (editor: Editor) => {
        return SYNC_EDITOR_TO_TRANSCRIPT_MAP.get(editor) ?? true
    },
    setSyncEditorToTranscriptEnabled: (editor: Editor, enabled: boolean) => {
        SYNC_EDITOR_TO_TRANSCRIPT_MAP.set(editor, enabled)
    },
    withoutSyncEditorToTranscript: (editor: Editor, fn: () => void) => {
        const enabled = TranscriptEditor.isSyncEditorToTranscriptEnabled(editor)
        TranscriptEditor.setSyncEditorToTranscriptEnabled(editor, false)
        fn()
        TranscriptEditor.setSyncEditorToTranscriptEnabled(editor, enabled)
    },

    getParagraphOffsetFromPoint: (editor: Editor, point: Point): number => {
        let offset = 0
        for (const [textNode, textPath] of Editor.nodes(editor, { at: [point.path[0]], match: Text.isText })) {
            if (Path.equals(textPath, point.path)) {
                return offset + point.offset
            }
            offset += textNode.text.length
        }

        return offset
    },
    /** Editable-character index of `point`, or `null` when it sits in a non-editable leaf. */
    editableOffsetFromPoint: (editor: Editor, point: Point): number | null => {
        let offset = 0
        for (const [textNode, textPath] of Editor.nodes(editor, { at: [point.path[0]], match: Text.isText })) {
            if (Path.equals(textPath, point.path)) {
                if (!textNode.isEditable) {
                    return null
                }

                return offset + point.offset
            }
            if (textNode.isEditable) {
                offset += textNode.text.length
            }
        }

        return null
    },
    /**
     * Move `selection` onto editable leaves. A point in a non-editable leaf goes to the nearer
     * editable edge, and the earlier edge when the distances match. A range that would cover a
     * non-editable leaf keeps its anchor and pulls the focus back to the anchor's side of that leaf.
     * Returns the same range when it is already valid, and null when the paragraph has no editable leaf.
     */
    clampSelectionToEditable: (editor: Editor, selection: Range): Range | null => {
        const spans = editableSpans(editor)
        if (spans.length === 0) {
            return null
        }

        const anchor = clampPointToEditable(editor, selection.anchor, spans)
        const focus = focusOffHole(editor, anchor, clampPointToEditable(editor, selection.focus, spans), spans)
        if (Point.equals(anchor, selection.anchor) && Point.equals(focus, selection.focus)) {
            return selection
        }

        return { anchor, focus }
    },
    /** Start or end of the editable leaves, or `null` when the paragraph has none. */
    editableEdgePoint: (editor: Editor, edge: 'start' | 'end'): Point | null => {
        const matches = [...Editor.nodes(editor, { at: [0], match: (node) => Text.isText(node) && node.isEditable })]
        if (matches.length === 0) {
            return null
        }

        const [, path] = edge === 'start' ? matches[0] : matches[matches.length - 1]

        return edge === 'start' ? Editor.start(editor, path) : Editor.end(editor, path)
    },
    getPointFromParagraphOffset: (editor: Editor, path: Path, offset: number): Point | null => {
        let currentOffset = 0
        for (const [textNode, textPath] of Editor.nodes(editor, { at: path, match: Text.isText })) {
            if (currentOffset + textNode.text.length >= offset) {
                return {
                    path: textPath,
                    offset: offset - currentOffset,
                }
            }
            currentOffset += textNode.text.length
        }

        return null
    },
    applyDelta: (editor: Editor, deltas: TranscriptDelta<string>[]) =>
        Editor.withoutNormalizing(editor, () =>
            TranscriptEditor.withoutSyncEditorToTranscript(editor, () => {
                let offset = 0
                for (const delta of deltas) {
                    if ('retain' in delta) {
                        offset += delta.retain ?? 0
                    }
                    if ('insert' in delta) {
                        const at = TranscriptEditor.getPointFromParagraphOffset(editor, [0], offset)
                        if (at) {
                            Transforms.insertText(editor, delta.insert!, { at })
                            offset += delta.insert!.length
                        }
                    }
                    if ('delete' in delta) {
                        const at = TranscriptEditor.getPointFromParagraphOffset(editor, [0], offset)
                        if (at) {
                            Transforms.delete(editor, { at, distance: delta.delete ?? 0, unit: 'character' })
                        }
                    }
                }
            }),
        ),
}
